import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSubscription } from '../hooks/useSubscription';
import { UpgradeModal } from './SubscriptionComponents';

const AITestSolver = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { questions, subject } = location.state || {};

    const [localQuestions, setLocalQuestions] = useState(questions || []);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [userAnswers, setUserAnswers] = useState({});
    const [score, setScore] = useState(0);
    const [isGenerating, setIsGenerating] = useState(false);
    const [showResults, setShowResults] = useState(false);
    const [feedback, setFeedback] = useState(null);
    const [attempts, setAttempts] = useState({});
    const [inputValue, setInputValue] = useState("");
    
    // Subscription
    const { checkLimit, updateUsage } = useSubscription();
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [requiredLevel, setRequiredLevel] = useState(1);

    useEffect(() => {
        setInputValue("");
    }, [currentIndex]);

    useEffect(() => {
        if (!localQuestions || localQuestions.length === 0) {
            navigate('/');
        }
    }, [localQuestions, navigate]);

    if (!localQuestions || localQuestions.length === 0) {
        return null;
    }

    const currentQuestion = localQuestions[currentIndex];
    const progress = ((currentIndex + 1) / localQuestions.length) * 100;

    const generateNewTest = async () => {
        if (!checkLimit()) {
            setShowUpgradeModal(true);
            return;
        }

        setIsGenerating(true);
        try {
            const token = localStorage.getItem('access_token');
            const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/ai-enhanced/generate-questions`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    subject: subject || 'math',
                    difficulty: 'easy',
                    question_count: 5
                })
            });

            if (response.status === 403) {
                const errorData = await response.json();
                setRequiredLevel(errorData.detail?.required_level || 1);
                setShowUpgradeModal(true);
                return;
            }

            if (!response.ok) throw new Error('Nepavyko sugeneruoti');
            const data = await response.json();
            
            // Update usage stats immediately to reflect token consumption
            if (data.usage_stats) {
                updateUsage(data.usage_stats);
            }
            
            setLocalQuestions(data.questions);
            setCurrentIndex(0);
            setUserAnswers({});
            setScore(0);
            setShowResults(false);
            setFeedback(null);
            setAttempts({});
            setInputValue('');
            
            // Pataisome React Router 'state', kad netyčia perkrovus puslapį 
            // neužkrautų pirminių senų klausimų.
            window.history.replaceState({ ...window.history.state, usr: { questions: data.questions, subject } }, '');
        } catch (err) {
            console.error(err);
            alert('Klaida generuojant naują testą. Bandyk dar kartą!');
        } finally {
            setIsGenerating(false);
        }
    };

    const handleAnswer = (answer) => {
        const isCorrect = answer === currentQuestion.correct_answer;
        const attemptCount = attempts[currentIndex] || 0;

        if (isCorrect) {
            const points = Math.max(10 - attemptCount * 2, 5);
            setScore(score + points);
            setUserAnswers({ ...userAnswers, [currentIndex]: answer });
            setFeedback({
                isCorrect: true,
                message: '🎉 Puiku! Teisingai!',
                points
            });

            setTimeout(() => {
                if (currentIndex < localQuestions.length - 1) {
                    setCurrentIndex(currentIndex + 1);
                    setFeedback(null);
                } else {
                    setShowResults(true);
                }
            }, 1500);
        } else {
            setAttempts({ ...attempts, [currentIndex]: attemptCount + 1 });
            setFeedback({
                isCorrect: false,
                message: attemptCount < 2 ? '🤔 Ne visai! Pabandyk dar kartą!' : `Teisingas atsakymas: ${currentQuestion.correct_answer}`
            });
            // Jei neteisingai, išvalome input'ą, kad būtų galima lengviau įvesti iš naujo
            if (currentQuestion.type === 'input' || !currentQuestion.options) {
                setInputValue("");
            }

            if (attemptCount >= 2) {
                setTimeout(() => {
                    if (currentIndex < localQuestions.length - 1) {
                        setCurrentIndex(currentIndex + 1);
                        setFeedback(null);
                    } else {
                        setShowResults(true);
                    }
                }, 2000);
            } else {
                setTimeout(() => {
                    setFeedback(null);
                }, 1500);
            }
        }
    };

    const getScoreEmoji = () => {
        const percentage = (score / (localQuestions.length * 10)) * 100;
        if (percentage >= 90) return '🏆';
        if (percentage >= 70) return '⭐';
        if (percentage >= 50) return '👍';
        return '💪';
    };

    if (showResults) {
        const maxScore = localQuestions.length * 10;
        const percentage = Math.round((score / maxScore) * 100);

        return (
            <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 p-8">
                <div className="max-w-2xl mx-auto">
                    <div className="bg-white rounded-3xl shadow-2xl p-12 text-center">
                        <div className="text-9xl mb-6">{getScoreEmoji()}</div>
                        <h2 className="text-4xl font-bold text-gray-800 mb-4">Testas baigtas!</h2>

                        <div className="bg-gradient-to-r from-purple-100 to-pink-100 rounded-2xl p-8 mb-6">
                            <p className="text-6xl font-bold text-purple-600 mb-2">{score} / {maxScore}</p>
                            <p className="text-2xl text-gray-700">Tavo rezultatas: {percentage}%</p>
                        </div>

                        <div className="grid grid-cols-2 gap-4 mb-8">
                            <div className="bg-green-100 rounded-xl p-4">
                                <p className="text-3xl font-bold text-green-600">
                                    {Object.entries(userAnswers).filter(([idx, ans]) => ans === localQuestions[idx]?.correct_answer).length}
                                </p>
                                <p className="text-sm text-gray-600">Įveikta klausimų</p>
                            </div>
                            <div className="bg-red-100 rounded-xl p-4">
                                <p className="text-3xl font-bold text-red-600">
                                    {Object.values(attempts).reduce((acc, curr) => acc + curr, 0)}
                                </p>
                                <p className="text-sm text-gray-600">Klaidingi bandymai</p>
                            </div>
                        </div>

                        <div className="flex gap-4">
                            <button
                                onClick={() => navigate('/')}
                                className="flex-1 px-8 py-4 bg-gray-200 text-gray-700 rounded-2xl font-bold text-lg hover:bg-gray-300 transition"
                            >
                                ← Grįžti
                            </button>
                            <button
                                onClick={generateNewTest}
                                disabled={isGenerating}
                                className="flex-1 px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition transform hover:scale-105 disabled:opacity-50 flex items-center justify-center gap-2"
                            >
                                {isGenerating ? (
                                    <><span className="animate-spin">⚙️</span> Generuojama...</>
                                ) : (
                                    <>🔄 Generuoti naują</>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-yellow-50 via-pink-50 to-purple-50 p-8">
            <div className="max-w-4xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-3xl shadow-2xl p-6 mb-6">
                    <div className="flex justify-between items-center mb-4">
                        <button
                            onClick={() => navigate('/')}
                            className="px-6 py-3 bg-gray-200 rounded-xl hover:bg-gray-300 transition font-bold"
                        >
                            ← Atgal
                        </button>
                        <div className="text-center">
                            <p className="text-2xl font-bold text-purple-600">Taškai: {score}</p>
                            <p className="text-sm text-gray-600">Klausimas {currentIndex + 1} / {localQuestions.length}</p>
                        </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="w-full bg-gray-200 rounded-full h-3">
                        <div
                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                        />
                    </div>
                </div>

                {/* Question Card */}
                <div className="bg-white rounded-3xl shadow-2xl p-12">
                    {currentQuestion.emoji_hint && (
                        <div className="text-8xl text-center mb-6">{currentQuestion.emoji_hint}</div>
                    )}

                    <h2 className="text-3xl font-bold text-gray-800 mb-8 text-center">
                        {currentQuestion.question}
                    </h2>

                    {/* Answer Options or Input */}
                    {currentQuestion.type === 'input' || !currentQuestion.options ? (
                        <div className="flex flex-col items-center gap-4 mb-6">
                            <input 
                                type="text"
                                value={inputValue}
                                onChange={(e) => setInputValue(e.target.value)}
                                onKeyDown={(e) => {
                                    if(e.key === 'Enter' && inputValue.trim()) {
                                        handleAnswer(inputValue.trim());
                                    }
                                }}
                                disabled={feedback !== null}
                                placeholder="Įrašyk atsakymą..."
                                className="w-full max-w-md p-4 text-2xl font-bold border-4 border-purple-300 rounded-2xl focus:border-purple-500 focus:outline-none text-center shadow-inner"
                            />
                            <button
                                onClick={() => handleAnswer(inputValue.trim())}
                                disabled={feedback !== null || !inputValue.trim()}
                                className="w-full max-w-md p-4 text-xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl shadow-lg hover:shadow-xl transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                Patikrinti atsakymą
                            </button>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 mb-6">
                            {currentQuestion.options?.map((option, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => handleAnswer(option)}
                                    disabled={feedback !== null}
                                    className="p-6 text-2xl font-bold bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl shadow-lg hover:shadow-xl transition transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {option}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Feedback */}
                    {feedback && (
                        <div className={`p-6 rounded-2xl text-center text-xl font-bold ${feedback.isCorrect ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                            }`}>
                            {feedback.message}
                            {feedback.points && <span className="ml-2">+{feedback.points} taškai!</span>}
                        </div>
                    )}

                    {/* Hint Button */}
                    {!feedback && currentQuestion.hint && (
                        <button
                            className="w-full mt-4 px-6 py-3 bg-yellow-100 text-yellow-700 rounded-xl font-bold hover:bg-yellow-200 transition"
                        >
                            💡 Užuomina: {currentQuestion.hint}
                        </button>
                    )}
                </div>
            </div>
            {showUpgradeModal && (
                <UpgradeModal 
                    isOpen={showUpgradeModal} 
                    onClose={() => setShowUpgradeModal(false)} 
                    requiredLevel={requiredLevel}
                />
            )}
        </div>
    );
};

export default AITestSolver;
